/**
 * System-landscape diagram - the "current technology stack" topology config and the
 * questionnaire -> selections builder. Moved here from `apps/web` so the web diagram and
 * the server-side SOW renderer build identical selections from the same topology.
 *
 * The selection builder takes a minimal `TechStackAnswerSource` so this module stays
 * dependency-light (no web questionnaire helpers); web and api each adapt their own data
 * shape to it.
 */
export function resolvePlanTone(plan, planLabel) {
    const normalized = `${planLabel.toLowerCase()} ${plan.toLowerCase()}`;
    if (normalized.includes('sunset') || normalized.includes('deprecate'))
        return 'danger';
    if (normalized.includes('keep') || normalized.includes('retain'))
        return 'success';
    if (normalized.includes('migrate') || normalized.includes('replace'))
        return 'warning';
    return 'neutral';
}
export const TECH_STACK_ROW_MAPPING = [
    { toolKey: 'tech_stack_erp_tool', planKey: 'tech_stack_erp_plan', categoryId: 'erp', label: 'ERP' },
    { toolKey: 'tech_stack_crm_tool', planKey: 'tech_stack_crm_plan', categoryId: 'crm', label: 'CRM' },
    {
        toolKey: 'tech_stack_payroll_tool',
        planKey: 'tech_stack_payroll_plan',
        categoryId: 'payroll',
        label: 'Payroll',
    },
    {
        toolKey: 'tech_stack_ap_automation_tool',
        planKey: 'tech_stack_ap_automation_plan',
        categoryId: 'ap-automation',
        label: 'AP Automation',
    },
    {
        toolKey: 'tech_stack_taxes_tool',
        planKey: 'tech_stack_taxes_plan',
        categoryId: 'taxes',
        label: 'Taxes',
    },
    {
        toolKey: 'tech_stack_billing_tool',
        planKey: 'tech_stack_billing_plan',
        categoryId: 'billing-engines',
        label: 'Billing Engines',
    },
    {
        toolKey: 'tech_stack_bi_analytics_tool',
        planKey: 'tech_stack_bi_analytics_plan',
        categoryId: 'bi-analytics',
        label: 'BI / Analytics',
    },
];
export const PLAN_LABELS = { keep: 'Keep', sunset: 'Sunset', tbd: 'TBD' };
export const currentTechnologyStackLandscapeConfig = {
    columns: [
        { id: 'left', label: 'Front Office' },
        { id: 'center', label: 'ERP' },
        { id: 'right', label: 'Downstream' },
    ],
    legendItems: [
        { label: 'Sunset', tone: 'danger' },
        { label: 'Keep', tone: 'success' },
        { label: 'TBD', tone: 'neutral' },
    ],
    placements: {
        crm: { column: 'left', row: 'top' },
        'billing-engines': { column: 'left', row: 'bottom' },
        erp: { column: 'center', row: 'middle' },
        taxes: {
            column: 'center',
            row: 'middle',
            mode: 'embedded',
            attachToId: 'erp',
            fallback: { column: 'center', row: 'middle' },
        },
        'bi-analytics': { column: 'center', row: 'bottom', offsetX: 16 },
        payroll: { column: 'right', row: 'top' },
        'ap-automation': { column: 'right', row: 'middle' },
    },
    relationships: [
        { id: 'crm-to-erp', sourceId: 'crm', targetId: 'erp', sourceHandle: 'right', targetHandle: 'left' },
        {
            id: 'billing-to-erp',
            sourceId: 'billing-engines',
            targetId: 'erp',
            sourceHandle: 'right',
            targetHandle: 'left',
        },
        {
            id: 'erp-to-payroll',
            sourceId: 'erp',
            targetId: 'payroll',
            sourceHandle: 'right',
            targetHandle: 'left',
            bidirectional: true,
        },
        {
            id: 'erp-to-ap-automation',
            sourceId: 'erp',
            targetId: 'ap-automation',
            sourceHandle: 'right',
            targetHandle: 'left',
            bidirectional: true,
        },
        {
            id: 'erp-to-bi-analytics',
            sourceId: 'erp',
            targetId: 'bi-analytics',
            sourceHandle: 'bottom',
            targetHandle: 'top',
        },
    ],
};
/**
 * Build the system-landscape selections from a questionnaire's tech-stack answers.
 * One selection per tech-stack row that has at least one tool selected.
 */
export function buildCurrentTechnologyStackSelections(source) {
    const result = [];
    for (const row of TECH_STACK_ROW_MAPPING) {
        const rawTools = source.getRawValue(row.toolKey);
        const tools = Array.isArray(rawTools)
            ? rawTools.filter((v) => typeof v === 'string' && v.trim().length > 0)
            : typeof rawTools === 'string' && rawTools.trim()
                ? [rawTools.trim()]
                : [];
        if (tools.length === 0)
            continue;
        const toolLabels = tools.map((v) => source.getOptionLabel(row.toolKey, v) ?? v).join(', ');
        const rawPlan = source.getRawValue(row.planKey);
        const plan = typeof rawPlan === 'string' && rawPlan.trim() ? rawPlan.trim() : null;
        const planLabel = plan ? (PLAN_LABELS[plan] ?? plan) : null;
        const tone = plan ? resolvePlanTone(plan, planLabel ?? plan) : 'neutral';
        result.push({
            id: row.categoryId,
            label: row.label,
            currentTool: toolLabels,
            postPlan: planLabel,
            tone,
        });
    }
    return result;
}
//# sourceMappingURL=config.js.map